import { type CSSProperties, useCallback, useEffect, useRef } from "react";
import { type Aspect, aspectOf, isWarm } from "./routePrewarm";

/**
 * Every page except /live lays its content out in percentages on top of one
 * tall background image. The wrapper therefore has to be exactly as tall as
 * that image renders, which only the image's intrinsic aspect ratio knows —
 * so the ratio is what we put on the box, and the height follows the width for
 * free. The wrapper stays transparent until the image has decoded to avoid a
 * flash of unpositioned artwork.
 *
 * Pass the background's src and even that is skipped whenever `routePrewarm`
 * has already decoded it: the intrinsic size is known before the first render,
 * so the wrapper renders sized, opaque and with the fade suppressed — making
 * the first painted frame the finished one.
 */
export function useBackgroundWrapper(src?: string) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  // Frozen at mount: a class or a height that changed underneath a page which is
  // already revealed would be a flicker, not an improvement.
  const warm = useRef(src ? isWarm(src) : false).current;
  const aspect = useRef(src ? aspectOf(src) : undefined).current;

  const sync = useCallback(() => {
    const wrapper = wrapperRef.current;
    const img = imageRef.current;
    if (!wrapper || !img) return;
    // Already sized — by the warm path, or by an earlier call to this one.
    if (wrapper.style.aspectRatio) return;
    if (img.naturalWidth && img.naturalHeight) {
      applyAspect(wrapper, { width: img.naturalWidth, height: img.naturalHeight });
    }
  }, []);

  const reveal = useCallback(() => {
    wrapperRef.current?.classList.add("loaded");
  }, []);

  useEffect(() => {
    const img = imageRef.current;
    if (img?.complete) {
      sync();
      reveal();
    }
    // Safari can restore this page from the back/forward cache with a wrapper
    // that was never sized; sizing it is idempotent, so this is just a net.
    window.addEventListener("pageshow", sync);
    return () => window.removeEventListener("pageshow", sync);
  }, [sync, reveal]);

  const onLoad = useCallback(() => {
    sync();
    reveal();
  }, [sync, reveal]);

  /** For the wrapper element: already `loaded` when the artwork is decoded. */
  const wrapperClassName = warm ? "bg-wrapper loaded no-fade" : "bg-wrapper";
  const wrapperStyle = aspect ? aspectStyle(aspect) : undefined;

  return { wrapperRef, imageRef, onLoad, wrapperClassName, wrapperStyle };
}

/**
 * The artwork's intrinsic ratio, which goes onto the wrapper twice.
 *
 * As `aspect-ratio` it is the height: give the box a width and the height
 * follows, with no measurement and nothing to redo on resize. As `--bg-aspect`
 * it is the same ratio handed to CSS as a number it can multiply — ticket 016's
 * mobile crop needs it to work out the widest canvas that still fits the
 * screen's height (`calc(100dvh * var(--bg-aspect))`), which is the one thing a
 * bare `aspect-ratio` cannot express.
 *
 * Both are the image's own two numbers rather than a ratio worked out here, so
 * no stylesheet carries a constant that has to be kept in step with the assets
 * — and the browser divides them at full precision.
 */
function ratioOf({ width, height }: Aspect): string {
  return `${width} / ${height}`;
}

/** All of a wrapper element these two paths touch. */
type Sizeable = { style: { aspectRatio: string; setProperty(name: string, value: string): void } };

/** Sizes a wrapper whose artwork has just finished decoding. */
export function applyAspect(wrapper: Sizeable, aspect: Aspect): void {
  const ratio = ratioOf(aspect);
  wrapper.style.aspectRatio = ratio;
  wrapper.style.setProperty("--bg-aspect", ratio);
}

/** The same sizing for a wrapper whose artwork was decoded before it mounted. */
export function aspectStyle(aspect: Aspect): CSSProperties {
  const ratio = ratioOf(aspect);
  return { aspectRatio: ratio, "--bg-aspect": ratio } as CSSProperties;
}
